import * as Localization from 'expo-localization';
import * as storage from '../../../utils/storage';
import { ActivityStore } from '../Activity/Activity';
import { CategoryStore } from '../Category/Category';
import { ContentStore } from '../Content/Content';
import { DEFAULT_LANGUAGE } from '../../../config/config';
import { ExerciseStore } from '../Exercise/Exercise';
import { FeatureStore } from '../Feature/Feature';
import { IAnyModelType, Instance, SnapshotOut, types } from 'mobx-state-tree';
import { LanguageStore } from '../Language/Language';
import { OnboardingModel } from '../Onboarding/Onboarding';
import { PathStore } from '../Path/Path';
import { PsychologistStore } from '../Psychologists/Psychologist';
import { QuestionStore } from '../Question/Question';
import { ReckoningStore } from '../Reckoning/Reckoning';
import { ResponseStore } from '../Response/ResponseStore';
import { SessionStore } from '../Session/Session';
import { SurveyStore } from '../Survey/Survey';
import { TechniqueModel } from '../Technique/TechniqueModel';
import { TreeNav } from '../TreeNav/TreeNav';
import { TreeStore } from '../TreeStore/TreeStore';
import { UserStore } from '../User/User';
import { isNull, isUndefined } from 'lodash';
const languageFallback = { 'fr': require('../Language/fr.json'), 'nl': require('../Language/nl.json'), 'en': require('../Language/en.json') };


export const RootStoreModel = types
  .model('RootStore')
  .props({
    language: types.optional(LanguageStore, () =>
      LanguageStore.create({
        language: DEFAULT_LANGUAGE,
        languageData: JSON.stringify(languageFallback[DEFAULT_LANGUAGE]),
        state: 'pending',
        languageRoot: DEFAULT_LANGUAGE,
        ready: false
      }),
    ),
    user: types.optional(UserStore, () => UserStore.create({
      user: null,
      anonymous: true,
      activationCode: null,
      needCollectFeedbackAfterProgramChanged: false,
      nbAISessions:0,
      nbAISessionsWithRequested:0,
      tabSelected: 'paths',
      stat: {
        nbSessionsDone: null,
        number_of_path_started: null,
        number_of_path_completed: null
      },
      state: 'pending',
      stateGetProfile: 'done',
      stateSetActivePath: 'done'
    })),
    technique: types.optional(TechniqueModel, () =>  TechniqueModel.create({
      techniqueIndex: 0,
      techniques: [],
      timerDuration: 0,
      stepVibrationFlag: false,
      guidedBreathingMode: 'disabled'
    })
    ),
    onboarding: types.optional(OnboardingModel, () => OnboardingModel.create({firstTime: true, dashboardIndex: 0})),
    reckoning: types.optional(ReckoningStore, () => ReckoningStore.create({reckoning: [], state: 'done'})),
    categories: types.optional(CategoryStore, () => CategoryStore.create({categories: [], state: 'done'})),
    content: types.optional(ContentStore, ()=> ContentStore.create({content: [], states: []})),
    path: types.optional(PathStore, ()=> PathStore.create({path: [], state: 'done'})),
    session: types.optional(types.late((): IAnyModelType => SessionStore), () => SessionStore.create({session: [], state: 'done'})),
    survey: types.optional(SurveyStore, ()=> SurveyStore.create({survey : [], states: []})),
    question: types.optional(QuestionStore, ()=> QuestionStore.create({question : [], states: []})),
    exercise: types.optional(ExerciseStore, ()=> ExerciseStore.create({exercise : [], states: []})),
    activity: types.optional(ActivityStore, ()=> ActivityStore.create({activities : [], states: []})),
    feature: types.optional(FeatureStore, () => FeatureStore.create({feature: []})),
    psychologist: types.optional(PsychologistStore, () => PsychologistStore.create({psychologists: [], state:'done', isManual: false} )),
    trees: types.optional(TreeStore, {}),
    // types.late used to fix typescript circular type error
    treeNav: types.optional(types.late((): IAnyModelType => TreeNav), {}),
    responses: types.optional(ResponseStore, () => ResponseStore.create()),
  })
  .actions(self => {
    const  initLanguage = async function() {
      const phoneLanguage = Localization.getLocales()?.[0]?.languageCode || DEFAULT_LANGUAGE;
      self.language.setLanguagePhone(phoneLanguage);
      let language: any;
      if (self.user && self.user.getUser && self.user.getUser.language) {
        language = self.user.getUser.language;
      } else {
        language = phoneLanguage;
      }
      await self.language.setLanguageRoot(language);
    };

    const register = async (userData : any, cb?: () => void) => {
      await self.user.register(userData);
      if(userData.user.language && userData.user.language !== self.language.language) {
        await self.language.setLanguage(userData.user.language);
      }
      if(cb){
        cb();
      }
      afterLoad();
    };

    const afterLoadAsync =  async () => {
      if(self.user.user && self.language.language && !self.user.anonymous) {


        await self.user.fetchProfile(self.language.language);
        self.path.fetchPathWithTodaySessionWithIdFromStore();
        self.user.fetchFaceScanResults(self.language.language);


      }
      migrateStores();
    };

    const afterLoad =  () =>  {
      afterLoadAsync();
      if(self.user.user && self.language.language && !self.user.anonymous) {
        self.technique.fetchTechniques(self.language.language);
        self.technique.fetchSounds(self.language.language);

        self.reckoning.getCurrentReckoning(self.language.language);
        self.categories.fetchCategories(self.language.language);
        self.exercise.fetchMyExercise(self.language.language);
        self.activity.fetchMyActivity(self.language.language);
        self.survey.fetchSpecialSurveys();
      }
      migrateStores();
    };

    // Below are migrations (connected users downloading the new app)
    const migrateStores =  () => {
      if(isNull(self.onboarding?.dashboardIndex) || isUndefined(self.onboarding?.dashboardIndex)) {
        self.onboarding.dashboardIndex=-1;
      }
    };

    const clear =  () => {
      self.user && self.user.clear();
      self.categories && self.categories.clear();
      self.path && self.path.clear();
      self.exercise && self.exercise.clear();
      self.survey && self.survey.clear();
      self.question && self.question.clear();
      self.content && self.content.clear();
      self.psychologist && self.psychologist.clear();
      self.feature && self.feature.clear();
      self.activity && self.activity.clear();
      self.reckoning && self.reckoning.clear();
      self.session && self.session.clear();
      self.trees && self.trees.clear();
      self.treeNav && self.treeNav.clear();
      self.responses && self.responses.clearResponses();
    };


    const firstConnexion = async () => {
      const value = await storage.loadString('doneOnboarding');
      const firstConnexion = !(value && value==='true');
      self.onboarding.set(firstConnexion);
    };

    return {
      afterCreate() {
        firstConnexion();
        const thisLocal = this;
        const initLanguage = async function() {
          await thisLocal.initLanguage();
          await self.language.fetchLanguages();
          self.language.fetchLanguage();
        };

        initLanguage();
      },
      initLanguage,
      register,
      afterLoad,
      clear
    };});

export type RootStoreModelType = typeof RootStoreModel;

/**
 * The RootStore instance.
 */
export interface IRootStore extends Instance<typeof RootStoreModel> {}

/**
 * The data of a rootStore?.
 */
export type RootStoreSnapshot = SnapshotOut<typeof RootStoreModel>;